/**
 * @fileoverview WebSocket Service
 * @description Manages WebSocket connections for real-time updates
 * @module @xtream/frontend/services/websocket
 *
 * This service handles:
 * - Connection management (connect, disconnect, reconnect)
 * - Service subscriptions (subscribe, unsubscribe)
 * - Event handling for status updates, progress, errors
 * - Mock mode for development without backend
 */

import { io, Socket } from 'socket.io-client';
import {
  IClientToServerEvents,
  IServerToClientEvents,
  IStatusUpdateEvent,
  IProgressUpdateEvent,
  IErrorEvent,
  ILogEvent,
  IProgressData,
  ServiceStatus,
} from '@xtream/shared-types';
import { generateMockProgress, mockProgressData, mockServices } from '@xtream/shared-utils';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type TypedSocket = Socket<IServerToClientEvents, IClientToServerEvents>;

export interface SocketServiceOptions {
  /** Whether to use mock events instead of real WebSocket */
  useMocks?: boolean;
  /** Mock event interval in milliseconds */
  mockInterval?: number;
  /** Auto-reconnect attempts */
  reconnectionAttempts?: number;
  /** Reconnection delay in milliseconds */
  reconnectionDelay?: number;
}

export interface SocketServiceCallbacks {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onStatusUpdate?: (data: IStatusUpdateEvent) => void;
  onProgressUpdate?: (data: IProgressUpdateEvent) => void;
  onError?: (data: IErrorEvent) => void;
  onLog?: (data: ILogEvent) => void;
}

// Development-only logging helper
const devLog = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

const devError = (...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// EVENT LISTENER TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Union type for all possible event data types in the socket service.
 * This provides type safety for the generic event listener system.
 */
type SocketEventData =
  | IStatusUpdateEvent
  | IProgressUpdateEvent
  | IErrorEvent
  | ILogEvent
  | IProgressData
  | { serviceId: string; status: ServiceStatus }
  | { serviceId: string; progress: IProgressData }
  | string
  | Error
  | undefined;

// ═══════════════════════════════════════════════════════════════════════════
// SOCKET SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Singleton service for managing WebSocket connections
 * Provides real-time status and progress updates for transcoding services
 */
class SocketService {
  private socket: TypedSocket | null = null;
  private options: SocketServiceOptions;
  private callbacks: SocketServiceCallbacks = {};
  private listeners: Map<string, Set<(data: SocketEventData) => void>> = new Map();
  private subscribedServices: Set<string> = new Set();
  private mockIntervalId: ReturnType<typeof setInterval> | null = null;
  private isConnected = false;

  constructor(options: SocketServiceOptions = {}) {
    // FASE 14: Changed to use real WebSocket by default
    // Use mock only when VITE_USE_MOCK_API=true
    const defaultUseMocks = import.meta.env['VITE_USE_MOCK_API'] === 'true';

    this.options = {
      useMocks: defaultUseMocks,
      mockInterval: 1000,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      ...options,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONNECTION MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Connect to the WebSocket server
   * @param token - JWT token for authentication
   */
  connect(token: string): void {
    if (this.options.useMocks) {
      devLog('[SocketService] Using mock mode');
      this.isConnected = true;
      this.callbacks.onConnect?.();
      this.notifyListeners('connect', undefined);
      return;
    }

    // Don't reconnect if already connected
    if (this.socket && this.isConnected) {
      devLog('[SocketService] Already connected, skipping reconnect');
      return;
    }

    // Clean up existing connection that is in a bad state
    if (this.socket && !this.isConnected) {
      devLog('[SocketService] Cleaning up stale socket');
      this.socket.disconnect();
      this.socket = null;
    }

    const socketUrl =
      (import.meta.env['VITE_WS_URL'] as string | undefined) || window.location.origin;

    this.socket = io(`${socketUrl}/transcoding`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: this.options.reconnectionAttempts ?? 5,
      reconnectionDelay: this.options.reconnectionDelay ?? 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    if (this.options.useMocks) {
      this.stopMockEvents();
      this.isConnected = false;
      this.callbacks.onDisconnect?.('manual');
      this.notifyListeners('disconnect', 'manual');
      return;
    }

    if (this.socket) {
      // Unsubscribe from all services before disconnecting
      this.subscribedServices.forEach((serviceId) => {
        this.socket?.emit('unsubscribe:service', { serviceId });
      });
      this.subscribedServices.clear();

      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Check if connected to WebSocket server
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SUBSCRIPTION MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to updates for a specific service
   * @param serviceId - The service ID to subscribe to
   */
  subscribe(serviceId: string): void {
    if (this.subscribedServices.has(serviceId)) {
      return;
    }

    this.subscribedServices.add(serviceId);

    if (this.options.useMocks) {
      this.startMockEvents(serviceId);
      return;
    }

    if (this.socket && this.isConnected) {
      this.socket.emit('subscribe:service', { serviceId });
    }
  }

  /**
   * Unsubscribe from updates for a specific service
   * @param serviceId - The service ID to unsubscribe from
   */
  unsubscribe(serviceId: string): void {
    if (!this.subscribedServices.has(serviceId)) {
      return;
    }

    this.subscribedServices.delete(serviceId);

    if (this.options.useMocks) {
      if (this.subscribedServices.size === 0) {
        this.stopMockEvents();
      }
      return;
    }

    if (this.socket && this.isConnected) {
      this.socket.emit('unsubscribe:service', { serviceId });
    }
  }

  /**
   * Subscribe to updates for all services
   * @param serviceIds - Array of service IDs to subscribe to
   */
  subscribeToMany(serviceIds: string[]): void {
    serviceIds.forEach((id) => this.subscribe(id));
  }

  /**
   * Unsubscribe from all services
   */
  unsubscribeFromAll(): void {
    this.subscribedServices.forEach((id) => this.unsubscribe(id));
  }

  /**
   * Emit an event to the server
   * @param event - Event name (typed for known events, string for dynamic)
   * @param data - Event data (optional for events without parameters)
   */
  emit(event: keyof IClientToServerEvents | (string & {}), data?: unknown): void {
    if (this.options.useMocks) {
      // Mocks don't support custom events yet
      return;
    }

    if (this.socket && this.isConnected) {
      // Use type assertion for dynamic event names
      if (data !== undefined) {
        (this.socket.emit as (event: string, data: unknown) => void)(event as string, data);
      } else {
        (this.socket.emit as (event: string) => void)(event as string);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CALLBACK REGISTRATION
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Register callbacks for WebSocket events
   * @param callbacks - Object containing callback functions
   */
  setCallbacks(callbacks: SocketServiceCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Clear all registered callbacks
   */
  clearCallbacks(): void {
    this.callbacks = {};
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EVENT LISTENER MANAGEMENT (NEW)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Add an event listener
   * @param event - Event name
   * @param callback - Callback function
   */
  on<T extends SocketEventData = SocketEventData>(
    event: string,
    callback: (data: T) => void,
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback as (data: SocketEventData) => void);
  }

  /**
   * Remove an event listener
   * @param event - Event name
   * @param callback - Callback function
   */
  off<T extends SocketEventData = SocketEventData>(
    event: string,
    callback: (data: T) => void,
  ): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback as (data: SocketEventData) => void);
      if (callbacks.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Notify all listeners for an event
   */
  private notifyListeners(event: string, data: SocketEventData): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          devError(`[SocketService] Error in listener for ${event}:`, error);
        }
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRIVATE METHODS - EVENT LISTENERS
  // ─────────────────────────────────────────────────────────────────────────

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      devLog('[SocketService] Connected');
      this.isConnected = true;
      this.callbacks.onConnect?.();
      this.notifyListeners('connect', undefined);

      // Re-subscribe to any services we were subscribed to before disconnect
      this.subscribedServices.forEach((serviceId) => {
        this.socket?.emit('subscribe:service', { serviceId });
      });
    });

    this.socket.on('disconnect', (reason) => {
      devLog('[SocketService] Disconnected:', reason);
      this.isConnected = false;
      this.callbacks.onDisconnect?.(reason);
      this.notifyListeners('disconnect', reason);
    });

    this.socket.on('connect_error', (error) => {
      devError('[SocketService] Connection error:', error);
      this.notifyListeners('connect_error', error);
    });

    this.socket.on('status:update', (data: IStatusUpdateEvent) => {
      devLog('[SocketService] Status update:', data);
      this.callbacks.onStatusUpdate?.(data);
      this.notifyListeners('status:update', data);
    });

    this.socket.on('progress:update', (data: IProgressUpdateEvent) => {
      this.callbacks.onProgressUpdate?.(data);
      this.notifyListeners('progress:update', data);
    });

    this.socket.on('error', (data: IErrorEvent) => {
      devError('[SocketService] Error event:', data);
      this.callbacks.onError?.(data);
      this.notifyListeners('error', data);
    });

    this.socket.on('log', (data: ILogEvent) => {
      devLog('[SocketService] Log event:', data);
      this.callbacks.onLog?.(data);
      this.notifyListeners('log', data);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRIVATE METHODS - MOCK MODE
  // ─────────────────────────────────────────────────────────────────────────

  private startMockEvents(serviceId: string): void {
    // Don't start multiple intervals
    if (this.mockIntervalId) return;

    const runningService = mockServices.find(
      (s) => s.id === serviceId && s.status === ServiceStatus.RUNNING,
    );

    if (!runningService) {
      devLog('[SocketService Mock] No running service found for', serviceId);
      return;
    }

    devLog('[SocketService Mock] Starting mock events for', serviceId);

    let frameCount = 0;

    this.mockIntervalId = setInterval(() => {
      frameCount += 30;
      const progress = generateMockProgress({
        ...mockProgressData,
        frame: frameCount,
        time: this.formatTime(frameCount / 30),
      });

      // Only emit for subscribed services that are "running"
      this.subscribedServices.forEach((id) => {
        const service = mockServices.find((s) => s.id === id);
        if (service?.status === ServiceStatus.RUNNING) {
          this.callbacks.onProgressUpdate?.({
            serviceId: id,
            progress,
            timestamp: new Date().toISOString(),
          });
        }
      });
    }, this.options.mockInterval);
  }

  private stopMockEvents(): void {
    if (this.mockIntervalId) {
      clearInterval(this.mockIntervalId);
      this.mockIntervalId = null;
    }
  }

  private formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const frames = Math.floor((seconds % 1) * 30);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${frames.toString().padStart(2, '0')}`;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

export const socketService = new SocketService();

export default socketService;
